/**
 * LoopTrfButton — the family's "Loop in my case manager" control.
 *
 * TWO SHAPES, one behaviour. `variant="icon"` (the default since #1800) is a
 * small button in the composer's left toolbar, beside the attachment clip and
 * at the clip's weight. `variant="pill"` is the original amber outlined pill,
 * kept because the widget and any surface without that toolbar still need a
 * standalone control.
 *
 * Why the icon. The pill sat on its own slim row above the quick-prompt chips,
 * right-aligned, which made it the loudest element in the composer area for an
 * action a family reaches for once — if ever. A rare thread-level action
 * belongs beside the clip, at the clip's weight; the row it occupied goes away.
 *
 * NOT RENDERED TODAY. #1801 put every family-facing entry point to the
 * in-thread handoff behind the server-read `HANDOFF_FAMILY_ENTRY_ENABLED`
 * flag, which is dark by default; ChatPage's `showLoopTrfButton` is the gate
 * (it reads `family_entry_enabled` off `GET /api/handoffs/family-pending`).
 * The component, its route and its tests are kept intact so flipping the flag
 * on restores the affordance whole. Do not delete it.
 */
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CONTROL_GHOST } from '@/components/chat/composerControls';
import { api } from '@/services/api';
import { localizedApiErrorMessage } from '@/lib/utils';
import { serverMessage } from '@/lib/serverMessage';


interface LoopTrfButtonProps {
  conversationId: string;
  onCreated: () => void;
  onError?: (message: string) => void;
  /** 'icon' for the composer toolbar (#1800); 'pill' for a standalone row. */
  variant?: 'icon' | 'pill';
}

export function LoopTrfButton({
  conversationId,
  onCreated,
  onError,
  variant = 'icon',
}: LoopTrfButtonProps) {
  const { t, i18n } = useTranslation('chat');
  // The one wording, so the tooltip, the aria-label and the pill cannot drift.
  const LOOP_TRF_LABEL = t('handoff.loopIn.label');
  const LOOP_TRF_PENDING = t('handoff.loopIn.pending');
  const mutation = useMutation({
    mutationFn: () => api.handoffs.request({ conversation_id: conversationId, origin: 'chat_button' }),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      // B-181: an axios error's own .message is the generic "Request failed
      // with status code N" — the server's actionable text (e.g. "User has no
      // associated family") lives in response.data.message. Prefer it.
      // #2109 R3-B11: the server's text is English, so only an English reader
      // sees it; every other reader gets the localized generic line.
      const language = i18n.language ?? 'en';
      const readsEnglish = language === 'en' || language.startsWith('en-');
      const message = localizedApiErrorMessage(err, t, language)
        ?? (readsEnglish ? serverMessage(err, t('handoff.loopIn.failed')) : t('handoff.loopIn.failed'));
      onError?.(message);
    },
  });

  if (variant === 'pill') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-full border-status-warn-fg text-status-warn-fg hover:bg-status-warn"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? LOOP_TRF_PENDING : <>{'\u2192 '}{LOOP_TRF_LABEL}</>}
      </Button>
    );
  }

  // The pending state is the spinner and the disabled cursor, not a second
  // label: an icon button has nowhere to put "Looping your case manager in…"
  // without changing width and shoving the clip sideways. The tooltip and the
  // aria-label say it instead, so a screen reader hears the change too.
  const label = mutation.isPending ? LOOP_TRF_PENDING : LOOP_TRF_LABEL;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          aria-label={label}
          className={CONTROL_GHOST}
        >
          {mutation.isPending
            ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            : <UserPlus size={16} aria-hidden="true" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
